import * as React from 'react';
import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import { getCmsLocaleString } from 'js/lib/language';
import requestCountry from 'js/lib/requestCountry';
import Retracked from 'js/lib/retracked';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import LandingPageContentful from 'bundles/coursera-plus-landing-page/components/LandingPageContentful';
import NotEligibleForPromotionModal from 'bundles/coursera-plus-landing-page/components/promotions/NotEligibleForPromotionModal';
import {
  generateSubscriptionTiersLiteLandingPageVariant,
  isPreview,
} from 'bundles/coursera-plus-landing-page/contentful/constants';
import type {
  PropsForLandingPageQuery,
  PropsFromLandingPage,
} from 'bundles/coursera-plus-landing-page/contentful/fetchContentful';
import { withLandingPageData } from 'bundles/coursera-plus-landing-page/contentful/fetchContentful';
import EditorTool from 'bundles/coursera-plus-landing-page/editorTool/EditorTool';
import { getIsLearnerInEligibleCountry } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { isSubscriptionTiersOptedOut } from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import { useSubscriptionsByUser } from 'bundles/coursera-plus/utils/withSubscriptionsByUser';
import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import NotFound from 'bundles/phoenix/components/NotFound';
import InstallFriendbuyScript from 'bundles/referral/components/InstallFriendbuyScript';
import { REFERRAL_ELIGIBLE_COUNTRIES } from 'bundles/referral/constants';

import _t from 'i18n!nls/coursera-plus-landing-page';

export const INDIA_ANNUAL_PROMO_MID_2024_SLUG = 'coursera-annual-india-2024';

type PropsFromCaller = {};
export type PropsToComponent = PropsForLandingPageQuery & PropsFromLandingPage;

export const MarketingHome: React.FC<PropsToComponent> = (props, { _eventData }) => {
  const [showNotEligibleModal, setShowNotEligibleModal] = useState(false);
  const [notEligibleMessage, setNotEligibleMessage] = useState<string | undefined>();
  const { landingPage, slug } = props;
  const { countriesToExclude, countriesToInclude } = landingPage ?? {};
  const activatePurchaseTracker = !!landingPage?.activatePurchaseTracker;
  const { subscriptions } = useSubscriptionsByUser(!isSubscriptionTiersOptedOut());
  const isLearnerInIndia = requestCountry.get() === 'IN';
  const isFriendbuyReferralEligibleCountry = REFERRAL_ELIGIBLE_COUNTRIES.includes(requestCountry.get());
  const isLearnerInEligibleCountry = getIsLearnerInEligibleCountry({ countriesToExclude, countriesToInclude });

  useEffect(() => {
    const userPlusOrLiteSubscriptions =
      subscriptions?.filter(
        (subscription) => subscription.isCourseraPlusSubscription || subscription.isCourseraLiteSubscription
      ) ?? [];
    const isEligibleForFriendbuyPromotion =
      isSubscriptionTiersOptedOut() && isFriendbuyReferralEligibleCountry && userPlusOrLiteSubscriptions?.length === 0;

    const isEligibleForJune2023TentpolePromo =
      isSubscriptionTiersOptedOut() &&
      !isLearnerInIndia &&
      userPlusOrLiteSubscriptions.filter(({ isActive, isFreeTrial }) => isActive || isFreeTrial)?.length === 0;

    if (
      (activatePurchaseTracker && !isEligibleForFriendbuyPromotion) ||
      (slug === 'june-promo-2023' && !isEligibleForJune2023TentpolePromo)
    ) {
      setShowNotEligibleModal(true);
    }
  }, [activatePurchaseTracker, subscriptions, slug, isLearnerInIndia, isFriendbuyReferralEligibleCountry]);

  useEffect(() => {
    if (slug === 'offer' && !isLearnerInIndia) {
      generateSubscriptionTiersLiteLandingPageVariant();
    }
  }, [slug, isLearnerInIndia]);

  useEffect(() => {
    if (!isLearnerInEligibleCountry) {
      setShowNotEligibleModal(true);
      setNotEligibleMessage(_t('Sorry, this offer is currently not available in your region.'));
    }
  }, [isLearnerInEligibleCountry]);

  // todo: remove when route gets finalized
  if (slug === 'tooltemp') {
    if (courseraPlusExperiments.preview('contentfulEditor')) {
      return <EditorTool />;
    }
  }

  if (!landingPage) {
    return <NotFound />;
  }

  const closeNotEligibleModal = () => {
    redirect.setLocation('/courseraplus');
  };

  return (
    <>
      {showNotEligibleModal && (
        <NotEligibleForPromotionModal handleClose={closeNotEligibleModal} messageOverride={notEligibleMessage} />
      )}
      <InstallFriendbuyScript
        shouldLoadFriendbuyScript={Boolean(activatePurchaseTracker)}
        trackingContextData={_eventData}
      />
      {!showNotEligibleModal && <LandingPageContentful landingPage={landingPage} hideRegionalPromotionBanner={true} />}
    </>
  );
};

MarketingHome.contextTypes = {
  _eventData: PropTypes.object,
};

export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter<PropsForLandingPageQuery, PropsFromCaller>((router) => {
    return {
      slug: router.params.slug,
      preview: isPreview(router),
      locale: getCmsLocaleString(),
    };
  }),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'coursera_plus',
      page: 'coursera_plus_marketing_page',
    },
  })),
  withLandingPageData()
)(MarketingHome);
